import { useState, useEffect } from "react";
import axios from "axios";
import AsyncSelect from "react-select/async";
import { useParams } from "react-router-dom";
import Sidebar from "../../../components/sidebar/Sidebar";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { PackageTierSelect, PackageTierOptionsSelect, getPackageTier, isAppServiceTier, getPackageCreditRate } from "../../../utils/packages";
import { PAYMENT_METHODS } from "../../../utils/paymentMethods";
import { getSelectStyles } from "../../../utils/selectStyles";
import "../../accounts/create/CreateAccountPage.css";
import { useMask } from "../../../utils/privacyMask";

export default function EditSubscriptionPage() {
  const { id } = useParams();
  const { hidden } = useMask();

  const [accounts, setAccounts]                     = useState([]);
  const [defaultAccount, setDefaultAccount]         = useState(null);
  const [additionalAccounts, setAdditionalAccounts] = useState([]);
  const [packageTier, setPackageTier]               = useState("");
  const [loading, setLoading]                       = useState(true);
  const [submitting, setSubmitting]                 = useState(false);
  const [updated, setUpdated]                       = useState(false);
  const [error, setError]                           = useState(null);

  const [subscription, setSubscription] = useState({
    accountID: "", username: "", password: "", creditCost: 0,
    package: "", paidPrice: 0, profitReceived: 0,
    createdDate: null, expireDate: null, active: false, notes: "", paymentMethod: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const [subRes, accRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/subscription/${id}`, { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/account`, { headers }),
        ]);
        const sub = {
          ...subRes.data,
          createdDate: new Date(Number(subRes.data.createdDate)).getTime(),
          expireDate:  new Date(Number(subRes.data.expireDate)).getTime(),
        };
        setSubscription(sub);
        setAdditionalAccounts(subRes.data.additionalAccounts || []);
        setPackageTier(getPackageTier(sub.package));
        setAccounts(accRes.data);
        const match = accRes.data.find(a => a._id === sub.accountID);
        if (match) setDefaultAccount({ value: match._id, label: `${match.username}${match.email ? ` (${match.email})` : ''}` });
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load subscription");
      }
    };
    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'createdDate' || name === 'expireDate') {
      setSubscription(prev => ({ ...prev, [name]: value ? new Date(value).getTime() : null }));
    } else {
      setSubscription(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  const loadOptions = (inputValue, callback) => {
    const filtered = accounts.filter(a =>
      a.username.toLowerCase().includes(inputValue.toLowerCase()) ||
      a.email?.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(filtered.map(a => ({ value: a._id, label: `${a.username}${a.email ? ` (${a.email})` : ''}` })));
  };

  const formatDateForInput = (unixMs) => unixMs ? new Date(unixMs).toISOString().split('T')[0] : '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subscription.package) { setError("Please select a package option"); return; }
    setError(null);
    setUpdated(false);
    setSubmitting(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/subscription/update`,
        {
          ...subscription,
          additionalAccounts,
          package: Number(subscription.package),
          createdDate: subscription.createdDate + "",
          expireDate:  subscription.expireDate + "",
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setUpdated(true);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update subscription");
    } finally {
      setSubmitting(false);
    }
  };

  const isAppService = packageTier ? isAppServiceTier(packageTier) : false;

  // ── Cost calculation (IPTV only) ──────────────────────────────────────────
  const creditRate   = getPackageCreditRate(packageTier);
  const credits      = Number(subscription.creditCost);
  const totalCost    = creditRate != null && credits > 0 ? credits * creditRate : null;
  const revenue      = Number(subscription.paidPrice);
  const profit       = totalCost != null && revenue > 0 ? revenue - totalCost : null;

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <Sidebar title="Edit Subscription" />
      <div className="form-page">
        <div className="form-panel">

          <div className="form-panel-header">
            <div>
              <div className="form-panel-title">Edit Subscription</div>
              <div className="form-panel-subtitle">ID: {subscription._id}</div>
            </div>
          </div>

          <div className="form-panel-body">
            <form onSubmit={handleSubmit} noValidate>

              {/* ── Account & Status ── */}
              <div className="form-section-label">Account & Status</div>
              <div className="row g-4 mb-2">
                <div className="col-md-8">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-person-fill" />Account</label>
                    <AsyncSelect cacheOptions defaultOptions loadOptions={loadOptions}
                      onChange={opt => { if (!opt) return; setDefaultAccount(opt); setSubscription(prev => ({ ...prev, accountID: opt.value })); }}
                      value={defaultAccount} placeholder="Search account…" isClearable styles={getSelectStyles()} />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-power" />Status</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: 42 }}>
                      <input type="checkbox" id="active" name="active" checked={!!subscription.active}
                        onChange={handleChange} style={{ width: 18, height: 18, accentColor: '#4F46E5', cursor: 'pointer' }} />
                      <label htmlFor="active" style={{ color: subscription.active ? '#7ab88e' : '#c47070', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer', margin: 0 }}>
                        {subscription.active ? 'Active' : 'Inactive'}
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Package ── */}
              <div className="row g-4 mb-2">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-box-seam" />Package Type</label>
                    <select className="form-input" value={packageTier}
                      onChange={e => { setPackageTier(e.target.value); setSubscription(prev => ({ ...prev, package: "" })); }}>
                      <PackageTierSelect />
                    </select>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-list-ul" />Package Option</label>
                    <select className="form-input" name="package" value={subscription.package} onChange={handleChange} disabled={!packageTier}>
                      <PackageTierOptionsSelect tier={packageTier} />
                    </select>
                  </div>
                </div>
              </div>

              {/* ── Credentials (IPTV only) ── */}
              {!isAppService && (
                <>
                  <div className="form-section-label" style={{ marginTop: 8 }}>Credentials</div>
                  <div className="row g-4 mb-2">
                    <div className="col-md-4">
                      <div className="form-field">
                        <label className="form-field-label"><i className="bi bi-person-badge" />Username</label>
                        <input className="form-input" type="text" name="username" value={subscription.username} onChange={handleChange} autoComplete="off" />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-field">
                        <label className="form-field-label"><i className="bi bi-key" />Password</label>
                        <input className="form-input" type="text" name="password" value={subscription.password} onChange={handleChange} autoComplete="off" />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-field">
                        <label className="form-field-label"><i className="bi bi-coin" />Credits</label>
                        <input className="form-input" type="number" name="creditCost" value={subscription.creditCost} onChange={handleChange} min="0" />
                        {totalCost !== null && (
                          <p className="form-cost-hint">
                            <i className="bi bi-calculator" />
                            {hidden ? '••••••' : (<>{credits} credits × €{creditRate.toFixed(2)} = <span className="hint-val">€{totalCost.toFixed(2)} cost</span></>)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* ── Financials ── */}
              <div className="form-section-label" style={{ marginTop: 8 }}>Financials</div>
              <div className="row g-4 mb-2">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-cash-stack" />Paid Price</label>
                    <div className="form-input-group">
                      <span className="form-input-prefix">€</span>
                      <input className="form-input" type="number" name="paidPrice" value={subscription.paidPrice} onChange={handleChange} step="0.01" />
                    </div>
                    {profit !== null && (
                      <p className={`form-cost-hint ${profit >= 0 ? 'hint-profit-pos' : 'hint-profit-neg'}`}>
                        <i className="bi bi-graph-up-arrow" />
                        {hidden ? '••••••' : (<>€{revenue.toFixed(2)} − €{totalCost.toFixed(2)} cost = <span className="hint-val">€{profit.toFixed(2)} profit</span></>)}
                      </p>
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-graph-up-arrow" />Profit Received</label>
                    <div className="form-input-group">
                      <span className="form-input-prefix">€</span>
                      <input className="form-input" type="number" name="profitReceived" value={subscription.profitReceived} onChange={handleChange} step="0.01" />
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-credit-card" />Payment Method</label>
                    <select className="form-input" name="paymentMethod" value={subscription.paymentMethod || ""} onChange={handleChange}>
                      <option value="">Select method…</option>
                      {PAYMENT_METHODS.map(m => (
                        <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* ── Dates ── */}
              <div className="form-section-label" style={{ marginTop: 8 }}>Dates</div>
              <div className="row g-4 mb-2">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-calendar-plus" />Start Date</label>
                    <input className="form-input" type="date" name="createdDate" value={formatDateForInput(subscription.createdDate)} onChange={handleChange} />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-calendar-x" />Expire Date</label>
                    <input className="form-input" type="date" name="expireDate" value={formatDateForInput(subscription.expireDate)} onChange={handleChange} />
                  </div>
                </div>
              </div>

              {/* ── Additional Accounts (IPTV only) ── */}
              {!isAppService && (
                <>
                  <div className="form-section-label" style={{ marginTop: 8 }}>Additional Accounts</div>
                  <div style={{ marginBottom: 16 }}>
                    {additionalAccounts.map((acc, i) => (
                      <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                        <input className="form-input" type="text" placeholder="Username" value={acc[0]}
                          onChange={e => { const u = [...additionalAccounts]; u[i][0] = e.target.value; setAdditionalAccounts(u); }} />
                        <input className="form-input" type="text" placeholder="Password" value={acc[1]}
                          onChange={e => { const u = [...additionalAccounts]; u[i][1] = e.target.value; setAdditionalAccounts(u); }} />
                        <button type="button" onClick={() => setAdditionalAccounts(prev => prev.filter((_, idx) => idx !== i))}
                          style={{ padding: '0 12px', borderRadius: 8, border: '1px solid rgba(139,58,58,0.3)', background: 'rgba(139,58,58,0.08)', color: '#c47070', cursor: 'pointer', flexShrink: 0 }}>
                          <i className="bi bi-x-lg" />
                        </button>
                      </div>
                    ))}
                    <button type="button" onClick={() => setAdditionalAccounts(prev => [...prev, ["", ""]])}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1px solid rgba(79, 70, 229,0.3)', background: 'rgba(79, 70, 229,0.08)', color: '#6366F1', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>
                      <i className="bi bi-plus-lg" /> Add Account
                    </button>
                  </div>
                </>
              )}

              {/* ── Notes ── */}
              <div className="form-section-label" style={{ marginTop: 8 }}>Notes</div>
              <div className="row g-4">
                <div className="col-12">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-journal-text" />Notes</label>
                    <textarea className="form-input" name="notes" value={subscription.notes || ''} onChange={handleChange}
                      rows={3} placeholder="Additional notes…" style={{ resize: 'vertical' }} />
                  </div>
                </div>
              </div>

              {updated && <div className="form-banner success"><i className="bi bi-check-circle-fill" />Subscription updated successfully!</div>}
              {error   && <div className="form-banner error"><i className="bi bi-exclamation-triangle-fill" />{error}</div>}

              <div className="form-actions">
                <button type="button" className="btn-form-cancel" onClick={() => window.history.back()}>
                  <i className="bi bi-x-lg" />Cancel
                </button>
                <button type="submit" className="btn-form-submit" disabled={submitting}>
                  <i className={`bi ${submitting ? 'bi-hourglass-split' : 'bi-check-lg'}`} />
                  {submitting ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
